import React from "react";
import { View, Text, StyleSheet } from "react-native";

/**
 * AgreementRenderer
 * Renders blocks like:
 * meta, title, heading, subheading, part, chapter, section, paragraph, list, spacer
 *
 * Props:
 * - blocks: array of blocks
 * - maxItems: number (optional) - renders only first N blocks (for preview)
 */

function splitAtFirstColon(text = "") {
  const idx = text.indexOf(":");
  if (idx === -1) return { head: "", tail: text };
  return {
    head: text.slice(0, idx + 1), // includes colon
    tail: text.slice(idx + 1), // after colon
  };
}

function splitSectionPrefix(text = "") {
  // Only style the "SECTION X:" part (case-insensitive)
  const match = text.match(/^(SECTION\s+\d+\s*:\s*)(.*)$/i);
  if (!match) return { prefix: "", rest: text };
  return { prefix: match[1], rest: match[2] };
}

export default function AgreementRenderer({ blocks = [], maxItems }) {
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  const sliced =
    typeof maxItems === "number" ? safeBlocks.slice(0, maxItems) : safeBlocks;

  return (
    <View>
      {sliced.map((b, idx) => {
        if (!b || typeof b !== "object") return null;

        switch (b.type) {
          case "spacer":
            return <View key={idx} style={styles.spacer} />;

          case "meta":
            return (
              <Text key={idx} style={styles.meta}>
                {b.text}
              </Text>
            );

          case "title":
            return (
              <Text key={idx} style={styles.title}>
                {b.text}
              </Text>
            );

          case "heading":
            return (
              <Text key={idx} style={styles.heading}>
                {b.text}
              </Text>
            );

          case "subheading":
            return (
              <Text key={idx} style={styles.subheading}>
                {b.text}
              </Text>
            );

          case "part":
            return (
              <Text key={idx} style={styles.part}>
                {b.text}
              </Text>
            );

          case "chapter":
            return (
              <Text key={idx} style={styles.chapter}>
                {b.text}
              </Text>
            );

          case "section": {
            const { prefix, rest } = splitSectionPrefix(b.text || "");
            return (
              <Text key={idx} style={styles.sectionLine}>
                {!!prefix && (
                  <Text style={styles.sectionPrefixUnderline}>{prefix}</Text>
                )}
                <Text style={styles.sectionRest}>{rest}</Text>
              </Text>
            );
          }

          case "paragraph":
            return (
              <Text key={idx} style={styles.paragraph}>
                {b.text}
              </Text>
            );

          case "list": {
            const items = Array.isArray(b.items) ? b.items : [];
            return (
              <View key={idx} style={styles.listWrap}>
                {items.map((item, j) => {
                  const { head, tail } = splitAtFirstColon(item || "");
                  return (
                    <View key={`${idx}-${j}`} style={styles.bulletRow}>
                      <Text style={styles.bulletDot}>•</Text>

                      {head ? (
                        <Text style={styles.bulletText}>
                          <Text style={styles.listHead}>{head}</Text>
                          <Text style={styles.listTail}>{tail}</Text>
                        </Text>
                      ) : (
                        <Text style={styles.bulletText}>{item}</Text>
                      )}
                    </View>
                  );
                })}
              </View>
            );
          }

          default:
            // fallback: if you add new block types later
            return b.text ? (
              <Text key={idx} style={styles.paragraph}>
                {b.text}
              </Text>
            ) : null;
        }
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  spacer: { height: 12 },

  // Top “official” lines: centered, slightly smaller
  meta: {
    fontSize: 12.5,
    lineHeight: 18,
    textAlign: "center",
    color: "#111827",
    marginBottom: 4,
    fontWeight: "900",
  },

  // CERTIFIED TRUE COPY (big, bold, centered)
  title: {
    fontSize: 16,
    lineHeight: 22,
    textAlign: "center",
    fontWeight: "900",
    color: "#111827",
    letterSpacing: 0.4,
    marginBottom: 4,
    textTransform: "uppercase",
  },

  // LAW No... (bold centered)
  heading: {
    fontSize: 14.5,
    lineHeight: 21,
    textAlign: "center",
    fontWeight: "900",
    color: "#111827",
    marginBottom: 4,
    textTransform: "uppercase",
  },

  // RELATING TO... (bold centered)
  subheading: {
    fontSize: 13.5,
    lineHeight: 20,
    textAlign: "center",
    fontWeight: "800",
    color: "#111827",
    marginBottom: 6,
    textTransform: "uppercase",
  },

  // PART I (very bold, centered)
  part: {
    fontSize: 15.5,
    lineHeight: 22,
    textAlign: "center",
    fontWeight: "900",
    color: "#111827",
    marginTop: 10,
    marginBottom: 6,
    textTransform: "uppercase",
  },

  // CHAPTER / GENERAL PROVISIONS etc (bold, centered)
  chapter: {
    fontSize: 14,
    lineHeight: 21,
    textAlign: "center",
    fontWeight: "900",
    color: "#111827",
    marginTop: 6,
    marginBottom: 6,
    textTransform: "uppercase",
  },

  // Section line container (only prefix bold/underline)
  sectionLine: {
    fontSize: 13.5,
    lineHeight: 20,
    color: "#111827",
    marginTop: 6,
    marginBottom: 6,
  },

  // ✅ UPDATED: bold + underlined "SECTION X:"
  sectionPrefixUnderline: {
    fontWeight: "900",
    color: "#111827",
    textDecorationLine: "underline",
    textDecorationStyle: "solid",
  },

  // normal rest of the sentence
  sectionRest: {
    fontWeight: "400",
    color: "#111827",
  },

  paragraph: {
    fontSize: 13.5,
    lineHeight: 20,
    color: "#111827",
    marginBottom: 10,
  },

  listWrap: {
    marginTop: 2,
    marginBottom: 10,
    paddingLeft: 6,
  },
  bulletRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  bulletDot: {
    width: 16,
    fontSize: 14,
    lineHeight: 20,
    color: "#111827",
  },
  bulletText: {
    flex: 1,
    fontSize: 13.5,
    lineHeight: 20,
    color: "#111827",
  },

  // List label before ":" bold (e.g., "Anonymization:")
  listHead: {
    fontWeight: "900",
    color: "#111827",
  },
  listTail: {
    fontWeight: "400",
    color: "#111827",
  },
});
